import CryptoJS from "crypto-js";

/* ==================== Env ==================== */

// Secret key untuk encryptStorage
const STORAGE_KEY = import.meta.env.VITE_STORAGE as string;

// Base URL API
export const URL_API = import.meta.env.VITE_URL_API as string;

/* ==================== Utils ==================== */

/** Cek apakah variable kosong (undefined, null, atau string kosong) */
export const isEmpty = (variable: unknown): boolean =>
  [undefined, null, ""].includes(variable as any);

/* ==================== Crypto ==================== */

/** Encrypt string dengan AES + encodeURIComponent */
export function encryptData(value: string): string {
  const encrypted = CryptoJS.AES.encrypt(value, STORAGE_KEY).toString();
  return encodeURIComponent(encrypted);
}

/** Decrypt string yang sudah di-encryptData */
export function decryptData(value: string): string | null {
  try {
    const decodedValue = decodeURIComponent(value);
    const decrypted = CryptoJS.AES.decrypt(
      decodedValue,
      STORAGE_KEY
    ).toString(CryptoJS.enc.Utf8);

    return decrypted || null;
  } catch (err) {
    console.error("Failed to decrypt data:", err);
    return null;
  }
}

/* ==================== Error Helper ==================== */

type FormError =
  | string
  | Array<string | { value?: string }>
  | Record<string, unknown>
  | null
  | undefined;

/** Ambil pesan error pertama dari struktur FormError */
export function getFirstErrorMessage(err: FormError): string | null {
  if (!err) return null;

  // case string
  if (typeof err === "string") return err;

  // case array
  if (Array.isArray(err)) {
    const first = err[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    return first?.value ?? null;
  }

  // case object (tidak diketahui strukturnya)
  return null;
}

/* ==================== Image Crop ==================== */

export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropSize {
  width: number;
  height: number;
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
  size?: CropSize
): Promise<string> {
  const image = new Image();
  image.src = imageSrc;

  await new Promise<void>((resolve) => {
    image.onload = () => resolve();
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  // gunakan size jika ada, fallback ke ukuran crop
  canvas.width = size?.width ?? pixelCrop.width;
  canvas.height = size?.height ?? pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return canvas.toDataURL("image/jpeg");
}

export function redirectToSso() {
  const urlClient = import.meta.env.VITE_URL_SSO as string;
  const baseUrl = window.location.origin;
  const redirectUrl = `${urlClient}/login?oauthId=${encryptData(baseUrl)}`;
  window.location.href = redirectUrl;
}

export const parseNumber = (str: string): number | null => {
  if (!str) return null;
  const s = String(str).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
};

export const formatWithThousand = (intPart: string, separator: string = "."): string => String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

/** Format date to Indonesian locale */
export const formatDateIndo = (date?: string | null): string => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/** Convert number to Indonesian words (angka terbilang) */
export const angkaTerbilang = (num: number): string => {
  const units = ['', 'satu', 'dua', 'tiga', 'empat', 'lima', 'enam', 'tujuh', 'delapan', 'sembilan'];
  const teens = ['sepuluh', 'sebelas', 'dua belas', 'tiga belas', 'empat belas', 'lima belas', 'enam belas', 'tujuh belas', 'delapan belas', 'sembilan belas'];
  const tens = ['', '', 'dua puluh', 'tiga puluh', 'empat puluh', 'lima puluh', 'enam puluh', 'tujuh puluh', 'delapan puluh', 'sembilan puluh'];
  const hundreds = ['', 'seratus', 'dua ratus', 'tiga ratus', 'empat ratus', 'lima ratus', 'enam ratus', 'tujuh ratus', 'delapan ratus', 'sembilan ratus'];
  const thousands = ['', 'ribu', 'juta', 'miliar', 'triliun'];

  if (num === 0) return 'nol';

  let result = '';
  let group = 0;

  while (num > 0) {
    let chunk = num % 1000;
    if (chunk > 0) {
      let chunkStr = '';
      
      if (chunk >= 100) {
        chunkStr += hundreds[Math.floor(chunk / 100)] + ' ';
        chunk %= 100;
      }
      
      if (chunk >= 20) {
        chunkStr += tens[Math.floor(chunk / 10)] + ' ';
        chunk %= 10;
      }
      
      if (chunk >= 11 && chunk <= 19) {
        chunkStr += teens[chunk - 10] + ' ';
      } else if (chunk >= 10) {
        chunkStr += 'sepuluh ';
      } else if (chunk > 0) {
        if (chunk === 1 && group === 1) {
          chunkStr += 'se';
        } else {
          chunkStr += units[chunk] + ' ';
        }
      }
      
      if (group > 0) {
        chunkStr += thousands[group] + ' ';
      }
      
      result = chunkStr + result;
    }
    
    num = Math.floor(num / 1000);
    group++;
  }

  return result.trim();
};